import { useNetworkState } from "@uidotdev/usehooks";
import dayjs from "dayjs";
import React, { useCallback } from "preact/compat";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import ReactPlayer from "react-player/lazy";
import uniqid from 'uniqid';
import { init_flow, init_screen, pingScreen } from "../api/screen";
import Flow from "../components/Screen/Display/Flow";
import ErrorScreen from "../components/Screen/ErrorScreen";
import LoadingScreen from "../components/Screen/LoadingScreen";
import { last_refresh_day, root_rotate, screenToken, tokenId } from "../config/constant";
import { useAppContext } from "../context/AppContext";
import { sendDataToApp } from "../utils/android";
import { formatBytes, parseUrl } from "../utils/misc";
import { clearCache, getItem, setItem } from "../utils/storage";

const INTERVAL_DELAY_MS = 5000;
const FLOW_DELAY_MS = 2000;

const InitPlaylistContentScreen = ({ isDemo, appid, loadingScreen, preview_data, isReverse, reloadApp, translations, isApp, isLoadingScreen }) => {
  const { isOnline, hasError, isConfiguredScreen, isMercureWork, setHasError, configuredScreen, unconfiguredScreen } = useAppContext();

  const [screen, setScreen] = useState(null);
  const [flow, setFlow] = useState(null);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [nextFlow, setNextFlow] = useState(null);
  const [flowHistory, setFlowHistory] = useState({});
  const [blackMode, setBlackMode] = useState(false);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [percentLoaded, setPercentLoaded] = useState(0);
  const [company, setCompany] = useState(0);
  const [loadingNextFlow, setLoadingNextFlow] = useState({});
  const [performances, setPerformances] = useState(performance?.memory);
  const [helmetStyle, setHelmetStyle] = useState('');
  const [lastInitScreen, setLastInitScreen] = useState('');
  const [initTime, setInitTime] = useState(dayjs().format('YYYYMMDDHHmm'));
  const [imagesToLoad, setImagesToLoad] = useState(null);
  const flowHistoryRef = useRef(flowHistory);
  const nextFlowRef = useRef(nextFlow);
  const network = useNetworkState();
  const networkRef = useRef();
  const isMTKFireTV = /AFT/.test(navigator.userAgent);

  networkRef.current = network;
  nextFlowRef.current = nextFlow;
  flowHistoryRef.current = flowHistory;
  const displayPerf = preview_data && Object.keys(preview_data).includes("perf");

  const checkBlackMode = useCallback((first) => {
    const currentHour = Number.parseInt(dayjs().format("HHmm"));
    const hourBegin = Number.parseInt((screen?.config?.ecomode ?? [null, null])[1]);
    const hourEnd = Number.parseInt((screen?.config?.ecomode ?? [null, null])[0]);

    if (currentHour && hourBegin && hourEnd) {
      if (
        (hourBegin <= hourEnd && (currentHour >= hourBegin && currentHour < hourEnd)) ||
        (hourBegin > hourEnd && ((currentHour >= hourBegin && currentHour <= 2359) || (currentHour >= 0 && currentHour < hourEnd)))
      ) {
        setBlackMode(true);
      } else {
        setBlackMode(false);
      }
    } else {
      setBlackMode(false);
    }

    if (!first && isOnline) {
      ping();

      // Check refresh
      const currentDay = Number.parseInt(dayjs().format("YYYYMMDD"));
      const lastRefreshDay = Number.parseInt(getItem(last_refresh_day) ?? currentDay);
      const currentHour = Number.parseInt(dayjs().format("HHmm"));
      const hourRefresh = Number.parseInt(screen?.hourRefresh ?? 600);

      if (currentHour >= hourRefresh && (lastRefreshDay || 0) < currentDay && networkRef.current?.online) {
        if (isApp) {
          setTimeout(() => sendDataToApp('hard_reload'), 1000);
        } else {
          reloadApp();
        }
      }
    }
  }, [screen, isOnline, isApp, last_refresh_day]);

  const clearSession = useCallback(() => {
    setItem(screenToken, null);
    clearCache();
    reloadApp(true);
  });

  const ping = useCallback(() => {
    const token = getItem(screenToken);

    if (token && !isDemo) {
      pingScreen().then(response => {
        if (!response?.screen_exist) {
          clearSession();
        }
      }).catch(_ => {})
    }
  }, [screenToken, isDemo]);

  const getNextFlow = useCallback((id) => {
    if (!id) {
      // Return first
      setNextFlow({ ...flowHistoryRef.current[screen.flow.id], uniqId: uniqid() });
      return;
    }

    if (flowHistoryRef.current[id]) {
      // Check if already fetch
      setNextFlow({ ...flowHistoryRef.current[id], uniqId: uniqid() });
      return;
    }

    if (Object.keys(loadingNextFlow).includes(id)) {
      return;
    }

    loadingNextFlow[id] = true;
    setLoadingNextFlow(loadingNextFlow);

    init_flow(id, preview_data).then(flow => {
      const newFlow = preview_data?.preview_flow ? flow.flow : flow;
      setNextFlow({ ...newFlow, uniqId: uniqid() });

      flowHistoryRef.current[id] = newFlow;
      setFlowHistory(flowHistoryRef.current);
    }).catch((_) => {
      setNextFlow(flowHistoryRef.current[screen.flow.id] ? { ...flowHistoryRef.current[screen.flow.id], uniqId: uniqid() } : null);
    }).finally(() => {
      delete loadingNextFlow[id];
      setLoadingNextFlow(loadingNextFlow);
    });
  }, [screen, flowHistoryRef, loadingNextFlow]);

  const onEndFlow = useCallback(() => {
    // Clear all timeouts and intervals safely
    window.Timeout?.forEach(clearTimeout);
    window.Timeout = [];

    window.Interval?.forEach(clearInterval);
    window.Interval = [];

    if (nextFlowRef.current) {
      setCurrentFlow({ ...nextFlowRef.current, uniqId: uniqid() });

      const flowTimeout = setTimeout(() => {
        getNextFlow(nextFlowRef.current?.next?.id || screen.flow.id);
      }, FLOW_DELAY_MS);

      window.Timeout.push(flowTimeout); // Add to global Timeout array to track it
    }
  }, [nextFlowRef, screen]);

  const getFontFace = useCallback((fontUrl) => {
    const name = fontUrl.includes('-')
      ? fontUrl.split('/').pop().split('-')[0]
      : fontUrl.split('/').pop().split('.')[0];

    let weight = 'auto';

    if (fontUrl.includes('-Bold')) {
      weight = 700;
    } else if (fontUrl.includes('-SemiBold')) {
      weight = 600;
    } else if (fontUrl.includes('-Medium')) {
      weight = 500;
    } else if (fontUrl.includes('-Light')) {
      weight = 300;
    } else if (fontUrl.includes('-Thin')) {
      weight = 200;
    }

    return `@font-face {font-family: ${name};src: url('${fontUrl}') format('truetype');font-weight: ${weight};font-style: normal;font-display: block;}`;
  }, []);

  useEffect(() => {
    // Monitor the memory usage
    if (performances && performances.usedJSHeapSize > performances.jsHeapSizeLimit * 0.95) {
      console.warn("High memory usage detected");
      reloadApp();
    }
  }, [performances]);

  useEffect(() => {
    if (screen && !isDemo) {
      checkBlackMode(true);
      const blackModeInterval = setInterval(checkBlackMode, INTERVAL_DELAY_MS);

      return () => clearInterval(blackModeInterval);
    }
  }, [screen, checkBlackMode]);

  useEffect(() => {
    if (screen && !isMercureWork) {
      const UpdateScreenInterval = setInterval(() => {
        init_screen(preview_data, isDemo).then((response) => {
          setScreen(response);
        });
      }, INTERVAL_DELAY_MS);

      return () => clearInterval(UpdateScreenInterval);
    }
  }, [screen, isMercureWork]);

  useEffect(() => {
    if (!preview_data?.preview_flow) {
      sendDataToApp("init_screen", JSON.stringify({ screenToken: getItem(screenToken), screenId: getItem(tokenId), profiling: screen?.debugging ?? false }))
    }
  }, [preview_data, screen]);

  useEffect(() => {
    configuredScreen();
    setFlowHistory({});
    setAssetsLoaded(false);
    setPercentLoaded(0);

    init_screen(preview_data, isDemo).then((response) => {
      if (response.backgroundLinkApi) {
        sendDataToApp(response.backgroundLinkApi);
      }

      setFlow(response.flow)
      setScreen(response)
      setCompany(response.company)
      setLastInitScreen(dayjs().format("DD/MM/YYYY HH:mm"))
      setItem(last_refresh_day, Number.parseInt(dayjs().format("YYYYMMDD")))

      if (response.company?.displayConfig?.fonts?.length) {
        const tempHelmetStyle = response.company.displayConfig.fonts.map(getFontFace).join('');
        setHelmetStyle(tempHelmetStyle);
      }

      if ((!response.uuid || !response.token) && !preview_data.preview_flow && !isDemo) {
        setItem(screenToken, null);
        reloadApp();
        return null;
      }

      const { rotate = 0 } = response?.config?.root_config ?? {};
      const currentRotate = getItem(root_rotate);

      if (currentRotate != rotate) {
        setItem(root_rotate, `${rotate}`);
        reloadApp();
      }

      if (!response.flow?.id || !(response.flow?.areas?.length ?? 0)) {
        if (!!response.flow?.hasUnconfiguredAreas) {
          unconfiguredScreen();
        } else {
          setHasError(true);
        }

        return;
      }

      const nextFlowId = response.flow?.next?.id
      setImagesToLoad(response.flow?.areas[0]?.preloadPictures?.filter((url) => ['png', 'jpg', 'jpeg', 'gif', 'svg', 'ico', 'webp'].includes(parseUrl(url).pathname.split('.').pop())) ?? []);

      setCurrentFlow({ ...response.flow, isUniq: !response.flow?.next, uniqId: uniqid() })
      flowHistoryRef.current[response.flow.id] = response.flow
      setFlowHistory(flowHistoryRef.current)
      setTimeout(() => {
        if (nextFlowId) {
          getNextFlow(nextFlowId)
        }
      }, FLOW_DELAY_MS)
      document.getElementById("flow_container")?.requestFullscreen();
    }).catch((error) => {
      if (error.code === 404) {
        clearSession();
      } else {
        setHasError(true);
      }
    });
  }, [appid]);

  const blackListFrame = useCallback((areaSlug, params) => {
    if (!params) {
      return;
    }

    const tempCurrentFlow = params.isNext ? { ...nextFlow } : { ...currentFlow }
    const currentArea = currentFlow.areas.find((v) => v.slug === areaSlug)
    const data = [...currentArea.data]
    data.splice(params.currentFrame, 1)
    tempCurrentFlow.areas[0].data = data
    params.isNext ? setNextFlow(tempCurrentFlow) : setCurrentFlow(tempCurrentFlow)
    setFlowHistory({ [tempCurrentFlow.id]: tempCurrentFlow })
  }, [nextFlow, currentFlow]);

  const updateArea = useCallback((flowId, areaId, newArea) => {
    const areaIndex = flowHistory[flowId].areas?.findIndex((area) => area.id === areaId);
    flowHistory[flowId].areas[areaIndex] = newArea;
    setFlowHistory(flowHistory);
  }, [flowHistory]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPerformances(performance?.memory);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  if (isLoadingScreen) {
    return (<p>Loading</p>);
  }

  if (blackMode) {
    return (<div>
      <div id={"blackscreen"} />
      {!isApp ? <ReactPlayer
        className='hidden-video'
        url='/public/muted-blank.mp4'
        playing={true}
        width={10}
        height={10}
        muted={true}
        loop={true}
        config={{
          file: {
            hlsOptions: isMTKFireTV
              ? {
                  capLevelToPlayerSize: true,
                  startLevel: 2,
                  autoStartLoad: true,
                  abrEwmaDefaultEstimate: 500000,
                }
              : {},
            dashOptions: isMTKFireTV
              ? {
                  streaming: {
                    abr: {
                      autoSwitchBitrate: { video: false },
                      initialBitrate: { video: 1500 },
                    },
                  },
                }
              : {},
          },
        }}
        /> : null}
    </div>);
  }

  if (hasError) {
    return (<ErrorScreen isMissingData={!isConfiguredScreen} translations={translations} />);
  }

  if (currentFlow && assetsLoaded) {
    return (
      <div id={`flow_${currentFlow.id}`} key={`flow_${currentFlow.id}`} className={"flow_container"}>
        <Helmet>
          {helmetStyle?.length ?
            <style type="text/css">
              {helmetStyle}
            </style>
            : null}
          <title>{preview_data?.preview_flow ? '▶ Preview' : 'TV'} - {flow.name}</title>
        </Helmet>
        {(displayPerf && performances) ? (
          <div className='performances_container'>
            <p>Heure: {dayjs(new Date()).format('HH:mm')}</p>
            <p>Dernier refresh: {lastInitScreen}</p>
            <p>Last refresh day: {getItem(last_refresh_day)}</p>
            <p>Limite: {formatBytes(performances.jsHeapSizeLimit)}</p>
            <p>Total: {formatBytes(performances.totalJSHeapSize)} </p>
            <p>Utilisé: {formatBytes(performances.usedJSHeapSize)} </p>
            <p>Pourcentage: {Math.round(performances.totalJSHeapSize / performances.jsHeapSizeLimit * 100)}%</p>
            <p>Currently playing: {currentFlow ? currentFlow.id : '-'}</p>
          </div>
        ) : null}

        {(displayPerf && isApp) ? (
          <div className={"app_layout"}>
            <button type="button" onClick={(event) => {
              event.stopImmediatePropagation()
              sendDataToApp("ready")
            }}>Ready</button>
            <button type="button" onClick={(event) => {
              event.stopImmediatePropagation()
              sendDataToApp("reload")
            }}>Reload</button>
            <button type="button" onClick={(event) => {
              event.stopImmediatePropagation()
              sendDataToApp("hard_reload")
            }}>Hard reload</button>
            <button type="button" onClick={(event) => {
              event.stopImmediatePropagation()
              sendDataToApp("force_crash")
            }}>Exception Java</button>
          </div>
        ) : null}
        <Flow
          key={`flow_${currentFlow.id}`}
          network={network}
          setInitTime={setInitTime}
          isApp={isApp}
          translations={translations}
          updateArea={updateArea}
          preview_data={preview_data}
          isReverse={isReverse}
          company={company}
          flow={currentFlow}
          orientation={screen.orientation}
          onEnd={onEndFlow}
          blackListFrame={blackListFrame}
          nextFlow={nextFlow}
          flowHistory={flowHistory}
        />
      </div>
    )
  }

  return <LoadingScreen
    translations={translations}
    images={imagesToLoad}
    setAssetsLoaded={setAssetsLoaded}
    loadingScreen={loadingScreen}
    fade={false}
  />
}

InitPlaylistContentScreen.defaultProps = {
  preview_data: null
}

export default React.memo(InitPlaylistContentScreen);
